import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { ShoppingCart, Menu, X, MessageCircle, Globe, ChevronDown } from 'lucide-react';
import cartStore from '../utils/cartStore';

const Header = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t, isRTL } = useLanguage();
  const { currency, setCurrency, currencies } = useCurrency();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    updateCartCount();
    window.addEventListener('cartUpdated', updateCartCount);
    return () => window.removeEventListener('cartUpdated', updateCartCount);
  }, []);

  const updateCartCount = () => setCartCount(cartStore.getCartCount());
  const handleLogout = async () => { await logout(); navigate('/'); };
  const isAdmin = user && user.role === 'admin';

  return (
    <header className="fixed top-0 w-full z-50 bg-[#FCFBF8]/85 backdrop-blur-xl border-b border-[#EBE4D5] transition-all duration-300" data-testid="main-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="https://customer-assets.emergentagent.com/job_regal-collection-2/artifacts/87220c1a_Screenshot_2026-04-07_at_00.16.22-removebg-preview%20%281%29.png" alt="Studio Crochet" className="h-16 w-auto object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            <Link to="/" className="text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('home')}</Link>
            <Link to="/shop" className="text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('shop')}</Link>
            <Link to="/sale" className="text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('sale')}</Link>
            <Link to="/bestseller" className="text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('bestSeller')}</Link>
            <Link to="/about" className="text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('about')}</Link>
            <Link to="/contact" className="text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('contact')}</Link>
          </nav>

          {/* Right Icons */}
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
            {isAdmin && (
              <Link to="/admin" className="hidden lg:block text-[#1A1A18] hover:text-[#C29B57] transition-colors duration-300 font-outfit uppercase tracking-wider text-sm">{t('admin')}</Link>
            )}

            {/* Language & Currency Selector */}
            <div className="relative hidden lg:block">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-1.5 text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit text-sm"
                data-testid="lang-currency-toggle"
              >
                <Globe className="w-4 h-4" />
                <span>{lang.toUpperCase()} / {currencies[currency]?.symbol}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {showLangMenu && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-[#EBE4D5] rounded-md shadow-lg p-3 min-w-[180px] z-50" data-testid="lang-currency-menu">
                  <div className="mb-3">
                    <p className="font-outfit text-xs uppercase tracking-wider text-[#8C8577] mb-2">{t('language')}</p>
                    <div className="flex gap-2">
                      <button onClick={() => { setLang('en'); setShowLangMenu(false); }} className={`px-3 py-1.5 text-sm font-outfit rounded ${lang === 'en' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5] text-[#1A1A18]'}`} data-testid="lang-en">English</button>
                      <button onClick={() => { setLang('ar'); setShowLangMenu(false); }} className={`px-3 py-1.5 text-sm font-outfit rounded ${lang === 'ar' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5] text-[#1A1A18]'}`} data-testid="lang-ar">العربية</button>
                    </div>
                  </div>
                  <div>
                    <p className="font-outfit text-xs uppercase tracking-wider text-[#8C8577] mb-2">{t('currency')}</p>
                    <div className="flex gap-2">
                      <button onClick={() => { setCurrency('INR'); setShowLangMenu(false); }} className={`px-3 py-1.5 text-sm font-outfit rounded ${currency === 'INR' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5] text-[#1A1A18]'}`} data-testid="currency-inr">₹ INR</button>
                      <button onClick={() => { setCurrency('AED'); setShowLangMenu(false); }} className={`px-3 py-1.5 text-sm font-outfit rounded ${currency === 'AED' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5] text-[#1A1A18]'}`} data-testid="currency-aed">د.إ AED</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Talk to Us */}
            <a href="https://wa.me/919999633121?text=Hi%20Studio%20Crochet%2C%20I%20have%20a%20question." target="_blank" rel="noopener noreferrer" className="hidden lg:flex items-center gap-1.5 text-[#3A5A40] hover:text-[#C29B57] transition-colors duration-300 font-outfit text-sm" data-testid="talk-to-us-link">
              <MessageCircle className="w-4 h-4" />
              {t('talkToUs')}
            </a>

            {/* Cart */}
            <Link to="/cart" className="relative" data-testid="cart-icon">
              <ShoppingCart className="w-5 h-5 text-[#1A1A18] hover:text-[#C29B57] transition-colors" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C29B57] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-outfit" data-testid="cart-count">{cartCount}</span>
              )}
            </Link>

            {isAdmin && (
              <button onClick={handleLogout} className="text-[#8C8577] hover:text-[#C29B57] transition-colors duration-300 font-outfit text-xs uppercase tracking-wider" data-testid="logout-button">{t('logout')}</button>
            )}

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden" data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-[#EBE4D5]" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit uppercase tracking-wider text-sm">{t('home')}</Link>
              <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit uppercase tracking-wider text-sm">{t('shop')}</Link>
              <Link to="/sale" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit uppercase tracking-wider text-sm">{t('sale')}</Link>
              <Link to="/bestseller" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit uppercase tracking-wider text-sm">{t('bestSeller')}</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit uppercase tracking-wider text-sm">{t('about')}</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A18] hover:text-[#C29B57] transition-colors font-outfit uppercase tracking-wider text-sm">{t('contact')}</Link>

              {/* Mobile Lang/Currency */}
              <div className="pt-4 border-t border-[#EBE4D5] space-y-3">
                <div className="flex items-center gap-3">
                  <span className="font-outfit text-xs uppercase tracking-wider text-[#8C8577]">{t('language')}:</span>
                  <button onClick={() => setLang('en')} className={`px-3 py-1 text-sm font-outfit rounded ${lang === 'en' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5]'}`}>EN</button>
                  <button onClick={() => setLang('ar')} className={`px-3 py-1 text-sm font-outfit rounded ${lang === 'ar' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5]'}`}>AR</button>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-outfit text-xs uppercase tracking-wider text-[#8C8577]">{t('currency')}:</span>
                  <button onClick={() => setCurrency('INR')} className={`px-3 py-1 text-sm font-outfit rounded ${currency === 'INR' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5]'}`}>₹ INR</button>
                  <button onClick={() => setCurrency('AED')} className={`px-3 py-1 text-sm font-outfit rounded ${currency === 'AED' ? 'bg-[#C29B57] text-white' : 'border border-[#EBE4D5]'}`}>د.إ AED</button>
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
